"use client";
import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { ClockIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { cn } from "@/lib/utils";
import FocusTrap from "focus-trap-react";

// ============================================================================
// Types
// ============================================================================

export type OpeningHoursMode = "any" | "open_now" | "custom";

export interface OpeningHoursFilterPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Current mode selection */
  selectedMode: OpeningHoursMode;
  /** Callback when mode changes */
  onModeChange: (mode: OpeningHoursMode) => void;
  /** Selected day of week */
  selectedDay: string;
  /** Callback when day changes */
  onDayChange: (day: string) => void;
  /** Selected time */
  selectedTime: string;
  /** Callback when time changes */
  onTimeChange: (time: string) => void;
  /** Custom className for positioning */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const TIME_OPTIONS = [
  "12:00 AM",
  "12:30 AM",
  "1:00 AM",
  "1:30 AM",
  "2:00 AM",
  "2:30 AM",
  "3:00 AM",
  "3:30 AM",
  "4:00 AM",
  "4:30 AM",
  "5:00 AM",
  "5:30 AM",
  "6:00 AM",
  "6:30 AM",
  "7:00 AM",
  "7:30 AM",
  "8:00 AM",
  "8:30 AM",
  "9:00 AM",
  "9:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "1:00 PM",
  "1:30 PM",
  "2:00 PM",
  "2:30 PM",
  "3:00 PM",
  "3:30 PM",
  "4:00 PM",
  "4:30 PM",
  "5:00 PM",
  "5:30 PM",
  "6:00 PM",
  "6:30 PM",
  "7:00 PM",
  "7:30 PM",
  "8:00 PM",
  "8:30 PM",
  "9:00 PM",
  "9:30 PM",
  "10:00 PM",
  "10:30 PM",
  "11:00 PM",
  "11:30 PM",
];

// ============================================================================
// Component
// ============================================================================

export function OpeningHoursFilterPanel({
  isOpen,
  onClose,
  selectedMode,
  onModeChange,
  selectedDay,
  onDayChange,
  selectedTime,
  onTimeChange,
  className,
}: OpeningHoursFilterPanelProps) {
  const i18n = useI18N();
  const panelRef = useRef<HTMLDivElement>(null);
  const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);
  
  // Refs for tab buttons (for keyboard navigation)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  
  // Refs for day and time dropdown options (for keyboard navigation)
  const dayOptionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const timeOptionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isDayDropdownOpen) {
          setIsDayDropdownOpen(false);
        } else if (isTimeDropdownOpen) {
          setIsTimeDropdownOpen(false);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isDayDropdownOpen, isTimeDropdownOpen, onClose]);

  if (!isOpen) return null;

  const modes: { id: OpeningHoursMode; label: string }[] = [
    { id: "any", label: i18n.get("any") },
    { id: "open_now", label: i18n.get("openNow") },
    { id: "custom", label: i18n.get("custom") },
  ];

  // Handle mode change with analytics
  const handleModeChange = (mode: OpeningHoursMode) => {
    onModeChange(mode);
  };

  const handleDaySelect = (day: string) => {
    onDayChange(day);
    setIsDayDropdownOpen(false);
  };

  const handleTimeSelect = (time: string) => {
    onTimeChange(time);
    setIsTimeDropdownOpen(false);
  };

  // Handle keyboard navigation for tabs
  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number, totalCount: number) => {
    let targetIndex: number | null = null;

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        targetIndex = (index + 1) % totalCount; // Wrap around
        break;
      case "ArrowLeft":
        e.preventDefault();
        targetIndex = (index - 1 + totalCount) % totalCount; // Wrap around
        break;
      case "Home":
        e.preventDefault();
        targetIndex = 0;
        break;
      case "End":
        e.preventDefault();
        targetIndex = totalCount - 1;
        break;
      default:
        return;
    }

    if (targetIndex !== null && tabRefs.current[targetIndex]) {
      tabRefs.current[targetIndex]?.focus();
      // Activate the tab on arrow key press (common tab pattern)
      const targetMode = modes[targetIndex];
      if (targetMode) {
        handleModeChange(targetMode.id);
      }
    }
  };

  // Handle keyboard navigation for day dropdown options
  const handleDayOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number, totalCount: number) => {
    let targetIndex: number | null = null;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        targetIndex = index < totalCount - 1 ? index + 1 : index;
        break;
      case "ArrowUp":
        e.preventDefault();
        targetIndex = index > 0 ? index - 1 : index;
        break;
      case "Home":
        e.preventDefault();
        targetIndex = 0;
        break;
      case "End":
        e.preventDefault();
        targetIndex = totalCount - 1;
        break;
      default:
        return;
    }

    if (targetIndex !== null && dayOptionRefs.current[targetIndex]) {
      dayOptionRefs.current[targetIndex]?.focus();
    }
  };

  // Handle keyboard navigation for time dropdown options
  const handleTimeOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number, totalCount: number) => {
    let targetIndex: number | null = null;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        targetIndex = index < totalCount - 1 ? index + 1 : index;
        break;
      case "ArrowUp":
        e.preventDefault();
        targetIndex = index > 0 ? index - 1 : index;
        break;
      case "Home":
        e.preventDefault();
        targetIndex = 0;
        break;
      case "End":
        e.preventDefault();
        targetIndex = totalCount - 1;
        break;
      default:
        return;
    }

    if (targetIndex !== null && timeOptionRefs.current[targetIndex]) {
      timeOptionRefs.current[targetIndex]?.focus();
    }
  };

  return (
    <FocusTrap
      focusTrapOptions={{
        initialFocus: false,
        allowOutsideClick: true,
        returnFocusOnDeactivate: false,
        escapeDeactivates: false,
      }}
    >
      <div
        ref={panelRef}
        className={cn(
          "bg-bg-brand-secondary-default border border-neutral-100",
          "rounded-xl shadow-lg p-3 w-full md:w-[340px]",
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={i18n.get("openingHoursFilter")}
      >
      {/* Header */}
      <div className="flex items-center gap-4 py-2 px-0">
        <ClockIcon
          size={20}
          className="text-fg-neutral-strong flex-shrink-0"
          aria-hidden="true"
        />
        <h2 className="title-s-semibold text-fg-neutral-strong flex-1">
          {i18n.get("openingHours")}
        </h2>
      </div>

      {/* Mode Toggle */}
      <div className="flex flex-col gap-2 py-2">
        <div
          className={cn(
            "flex items-center gap-1 p-1",
            "border border-border-token-brand-minimal rounded-full",
          )}
          role="tablist"
          aria-label={i18n.get("selectOpeningHoursMode")}
        >
          {modes.map((mode, index) => {
            const isSelected = selectedMode === mode.id;
            return (
              <button
                key={mode.id}
                id={`opening-hours-tab-${mode.id}`}
                ref={(el) => {
                  if (el) tabRefs.current[index] = el;
                }}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-controls="opening-hours-tabpanel"
                onClick={() => handleModeChange(mode.id)}
                onKeyDown={(e) => handleTabKeyDown(e, index, modes.length)}
                className={cn(
                  "flex-1 flex items-center justify-center min-h-[36px] min-w-[44px] px-4 py-2 rounded-full transition-colors focus-blue",
                  "label-m-medium whitespace-nowrap",
                  isSelected
                    ? "bg-bg-brand-primary-default text-white"
                    : "text-fg-neutral-strong hover:bg-bg-brand-secondary-hover",
                )}
              >
                {mode.label}
              </button>
            );
          })}
        </div>

        {/* Custom Mode Options */}
        {selectedMode === "custom" && (
          <div
            id="opening-hours-tabpanel"
            role="tabpanel"
            aria-labelledby={`opening-hours-tab-${selectedMode}`}
            className="flex flex-col gap-2 mt-2"
          >
            {/* Day Selector */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsDayDropdownOpen(!isDayDropdownOpen);
                  setIsTimeDropdownOpen(false);
                }}
                className={cn(
                  "w-full flex items-center justify-between min-h-[56px] px-4 py-2",
                  "bg-bg-brand-secondary-default border border-border-token-neutral-subtle rounded-lg",
                  "hover:bg-bg-brand-secondary-hover transition-colors focus-blue",
                )}
                aria-haspopup="listbox"
                aria-expanded={isDayDropdownOpen}
                aria-label={`${i18n.get("selectDay")}: ${selectedDay || i18n.get("notSelected")}`}
              >
                <span className="label-m-medium text-fg-neutral-subtle">
                  {selectedDay || i18n.get("selectDay")}
                </span>
                <ChevronDown
                  size={24}
                  className={cn(
                    "text-fg-neutral-subtle transition-transform",
                    isDayDropdownOpen && "rotate-180",
                  )}
                  aria-hidden="true"
                />
              </button>

              {/* Day Dropdown */}
              {isDayDropdownOpen && (
                <div
                  className={cn(
                    "absolute top-full left-0 right-0 mt-1 z-10",
                    "bg-bg-brand-secondary-default border border-border-token-neutral-minimal",
                    "rounded-lg shadow-lg max-h-[200px] overflow-y-auto",
                  )}
                  role="listbox"
                  aria-label={i18n.get("selectDay")}
                >
                  {DAYS_OF_WEEK.map((day, index) => (
                    <button
                      key={day}
                      ref={(el) => {
                        if (el) dayOptionRefs.current[index] = el;
                      }}
                      type="button"
                      role="option"
                      aria-selected={selectedDay === day}
                      onClick={() => handleDaySelect(day)}
                      onKeyDown={(e) => handleDayOptionKeyDown(e, index, DAYS_OF_WEEK.length)}
                      className={cn(
                        "w-full text-left px-4 py-3 label-m-medium",
                        "hover:bg-bg-brand-secondary-hover transition-colors focus-blue",
                        selectedDay === day
                          ? "text-fg-brand-subtle bg-container-brand-minimal"
                          : "text-fg-neutral-subtle",
                      )}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Time Selector */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsTimeDropdownOpen(!isTimeDropdownOpen);
                  setIsDayDropdownOpen(false);
                }}
                className={cn(
                  "w-full flex items-center justify-between min-h-[56px] px-4 py-2",
                  "bg-bg-brand-secondary-default border border-border-token-neutral-subtle rounded-lg",
                  "hover:bg-bg-brand-secondary-hover transition-colors focus-blue",
                )}
                aria-haspopup="listbox"
                aria-expanded={isTimeDropdownOpen}
                aria-label={`${i18n.get("selectTime")}: ${selectedTime || i18n.get("notSelected")}`}
              >
                <span className="label-m-medium text-fg-neutral-subtle">
                  {selectedTime || i18n.get("selectTime")}
                </span>
                <ChevronDown
                  size={24}
                  className={cn(
                    "text-fg-neutral-subtle transition-transform",
                    isTimeDropdownOpen && "rotate-180",
                  )}
                  aria-hidden="true"
                />
              </button>

              {/* Time Dropdown */}
              {isTimeDropdownOpen && (
                <div
                  className={cn(
                    "absolute top-full left-0 right-0 mt-1 z-10",
                    "bg-bg-brand-secondary-default border border-border-token-neutral-minimal",
                    "rounded-lg shadow-lg max-h-[200px] overflow-y-auto",
                  )}
                  role="listbox"
                  aria-label={i18n.get("selectTime")}
                >
                  {TIME_OPTIONS.map((time, index) => (
                    <button
                      key={time}
                      ref={(el) => {
                        if (el) timeOptionRefs.current[index] = el;
                      }}
                      type="button"
                      role="option"
                      aria-selected={selectedTime === time}
                      onClick={() => handleTimeSelect(time)}
                      onKeyDown={(e) => handleTimeOptionKeyDown(e, index, TIME_OPTIONS.length)}
                      className={cn(
                        "w-full text-left px-4 py-3 label-m-medium",
                        "hover:bg-bg-brand-secondary-hover transition-colors focus-blue",
                        selectedTime === time
                          ? "text-fg-brand-subtle bg-container-brand-minimal"
                          : "text-fg-neutral-subtle",
                      )}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
    </FocusTrap>
  );
}

export default OpeningHoursFilterPanel;
